import "./init"

import * as Sentry from "@sentry/react"
import { inject } from "@vercel/analytics"
import ReactDOM from "react-dom/client"
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom"
import ApiKeysList from "./components/dashboard/ApiKeyList"
import BillingSettings from "./components/dashboard/BillingSettings"
import BusinessOwnerTools from "./components/dashboard/BusinessOwnerTools"
import CallbackNumberList from "./components/dashboard/CallbackNumberList"
import CallerSettings from "./components/dashboard/CallerSettings"
import CallHistory from "./components/dashboard/calls/CallHistory"
import ChooseTask from "./components/dashboard/calls/ChooseTask"
import FromDynamicTask from "./components/dashboard/calls/FromDynamicTask"
import NewCall from "./components/dashboard/calls/NewCall"
import PrankCall from "./components/dashboard/calls/PrankCall"
import ViewCall from "./components/dashboard/calls/ViewCall"
import CampaignCalls from "./components/dashboard/CampaignCalls"
import CampaignContacts from "./components/dashboard/CampaignContacts"
import CampaignLayout from "./components/dashboard/CampaignLayout"
import CampaignList from "./components/dashboard/CampaignList"
import Chat from "./components/dashboard/Chat"
import ChatGpt from "./components/dashboard/ChatGpt"
import ContactList from "./components/dashboard/ContactList"
import CountryRestriction from "./components/dashboard/CountryRestriction"
import DashboardPage from "./components/dashboard/Dashboard"
import DashboardLayout from "./components/dashboard/DashboardLayout"
import EditCampaign from "./components/dashboard/EditCampaign"
import EditContact from "./components/dashboard/EditContact"
import EditTaskTemplate from "./components/dashboard/EditTaskTemplate"
import IntegrationsList from "./components/dashboard/IntegrationsList"
import BusinessVerificationEdit from "./components/dashboard/BusinessVerificationEdit"
import ViewBusinessVerification from "./components/dashboard/ViewBusinessVerification"
import EditVerifiedBusiness from "./components/dashboard/EditVerifiedBusiness"
import MobileDownload from "./components/dashboard/MobileDownload"
import NewSubscription from "./components/dashboard/NewSubscription"
import RedirectToCalls from "./components/dashboard/RedirectToDashboard"
import SettingsLayout from "./components/dashboard/settings/SettingsLayout"
import ProfileSettings from "./components/dashboard/settings/ProfileSettings"
import PlanSettings from "./components/dashboard/settings/PlanSettings"
import VoiceSettings from "./components/dashboard/settings/VoiceSettings"
import ReviewPage from "./components/dashboard/ReviewPage"
import TaskTemplateList from "./components/dashboard/TaskTemplateList"
import TwilioPhoneNumberList from "./components/dashboard/TwilioPhoneNumberList"
import EditTwilioPhoneNumber from "./components/dashboard/EditTwilioPhoneNumber"
import UserSettings from "./components/dashboard/UserSettings"
import GetACall from "./components/GetACall"
import LandingCallEmbed from "./components/LandingCallEmbed"
import LoggedOutLayout from "./components/LoggedOutLayout"
import Logout from "./components/logout/Logout"
import PrivacyPolicy from "./components/PrivacyPolicy"
import ViewPublicCall from "./components/public/ViewPublicCall"
import SignupForm from "./components/SignupForm"
import TermsOfService from "./components/TermsOfService"
import "./index.css"
import ParameterizedErrorPage from "./components/dashboard/ParameterizedErrorPage"
import { NativeLayout } from "./components/NativeLayout"
import GlobalLayout from "./components/GlobalLayout"
import NewCallRedirect, { CallLibraryRedirect } from "./components/dashboard/calls/NewCallRedirect"
// import { initializeSmartBanner } from "./lib/smartbanner"
import CallTour from "./components/dashboard/CallTour"
import Referral from "./components/Referral"
import Gift from "./components/Gift"
import PromoCodeHandler from "./components/PromoCodeHandler"

inject()

// Initialize SmartBanner for non-native platforms
// initializeSmartBanner()

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

const router = sentryCreateBrowserRouter([
  {
    element: <GlobalLayout />,
    children: [
      {
        element: <NativeLayout />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: "settings",
                element: <SettingsLayout />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="/settings/profile" replace />
                  },
                  {
                    path: "profile",
                    element: <ProfileSettings />
                  },
                  {
                    path: "plan",
                    element: <PlanSettings />
                  },
                  {
                    path: "voice",
                    element: <VoiceSettings />
                  }
                ]
              },
              {
                path: "logout",
                element: <Logout />
              },
              {
                path: "review",
                element: <ReviewPage />
              },
              {
                path: "signup",
                element: <DashboardPage />
              },
              {
                path: "mobile",
                element: <DashboardPage />
              },
              {
                path: "dashboard",
                element: <DashboardPage />
              },
              {
                path: "mobile-app",
                element: <MobileDownload />
              },
              {
                path: "welcome",
                element: <CallTour />
              },
              {
                path: "chat",
                element: <Chat key="default" />
              },
              {
                path: "chat/:theme",
                element: <Chat key="with-theme" />
              },
              {
                path: "chatgpt",
                element: <ChatGpt />
              },
              {
                path: "calls/new",
                element: <NewCallRedirect />
              },
              {
                path: "new-call",
                element: <NewCall key="new-call" />
              },
              {
                path: "call-library",
                element: <ChooseTask />
              },
              {
                path: "calls/library",
                element: <CallLibraryRedirect />
              },
              {
                path: "calls/from-dynamic-task",
                element: <FromDynamicTask />
              },
              {
                path: "dynamic_tasks/:uuid/create_call",
                element: <FromDynamicTask />
              },
              {
                path: "calls/:callId",
                element: <ViewCall />
              },
              {
                path: "calls/:callId/edit",
                element: <NewCall key="call-edit" />
              },
              {
                path: "calls",
                element: <CallHistory />
              },
              {
                path: "caller-settings",
                element: <CallerSettings />
              },
              {
                path: "task-templates",
                element: <TaskTemplateList />
              },
              {
                path: "task-templates/new",
                element: <EditTaskTemplate />
              },
              {
                path: "task-templates/:uuid",
                element: <EditTaskTemplate />
              },
              {
                path: "api-keys",
                element: <ApiKeysList />
              },
              {
                path: "prank-calls",
                element: <PrankCall onComplete={() => {}} />
              },
              {
                path: "contacts",
                element: <ContactList />
              },
              {
                path: "contacts/new",
                element: <EditContact />
              },
              {
                path: "contacts/:uuid",
                element: <EditContact />
              },
              {
                path: "campaigns",
                element: <CampaignList />
              },
              // {
              //   path: "phone-numbers",
              //   element: <PhoneNumberList />
              // },
              // {
              //   path: "phone-numbers/:uuid",
              //   element: <EditPhoneNumber />
              // },
              {
                path: "phone-numbers",
                element: <TwilioPhoneNumberList />
              },
              {
                path: "phone-numbers/:uuid",
                element: <EditTwilioPhoneNumber />
              },
              {
                path: "business",
                element: <BusinessOwnerTools />
              },
              {
                path: "business/ai-receptionists",
                element: <BusinessOwnerTools />
              },
              {
                path: "business/verification/new",
                element: <BusinessVerificationEdit />
              },
              {
                path: "business/verification/:uuid",
                element: <ViewBusinessVerification />
              },
              {
                path: "business/edit/:uuid",
                element: <EditVerifiedBusiness />
              },
              {
                path: "callback-numbers",
                element: <CallbackNumberList />
              },
              {
                path: "billing-settings",
                element: <BillingSettings />
              },
              {
                path: "new-subscription",
                element: <NewSubscription />
              },
              {
                path: "integrations",
                element: <IntegrationsList />
              },
              {
                path: "user-settings",
                element: <UserSettings />
              },
              {
                element: <CampaignLayout />,
                children: [
                  {
                    path: "campaigns/new",
                    element: <EditCampaign />
                  },
                  {
                    path: "campaigns/:uuid",
                    element: <EditCampaign />
                  },
                  {
                    path: "campaigns/:uuid/contacts",
                    element: <CampaignContacts />
                  },
                  {
                    path: "campaigns/:uuid/calls",
                    element: <CampaignCalls />
                  }
                ]
              }
            ]
          },
          {
            element: <LoggedOutLayout />,
            children: [
              {
                path: "privacy",
                element: <PrivacyPolicy />
              },
              {
                path: "terms-of-service",
                element: <TermsOfService />
              },
              {
                path: "referral",
                element: <Referral />
              },
              {
                path: "gift",
                element: <Gift />
              },
              {
                path: "promo",
                element: <PromoCodeHandler />
              },
              {
                path: "/public-calls/:uuid",
                element: <ViewPublicCall />
              },
              {
                path: "*",
                element: <RedirectToCalls />
              },
              {
                path: "/landing-call",
                element: <LandingCallEmbed />
              },
              {
                path: "/signup-form",
                element: <SignupForm />
              },
              {
                path: "/get-a-call",
                element: <GetACall />
              },
              {
                path: "/country-restriction",
                element: <CountryRestriction />
              },
              {
                path: "/error",
                element: <ParameterizedErrorPage />
              }
            ]
          }
        ]
      }
    ]
  }
])

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
ReactDOM.createRoot(document.getElementById("root")!).render(<RouterProvider router={router} />)
